/**
 * NK Knowledge Engine — admin page.
 *
 * Four tabs:
 *   - Overview   : stat counters + last compose job progress
 *   - Facts      : searchable/filterable grid, inline create/edit, seed + CSV import
 *   - Composer   : pick facts (or auto), select templates, live progress bar
 *   - Variants   : review queue with fact context + validation badges
 *
 * Style intentionally mirrors /admin/questions (Question Bank) for consistency.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MagnifyingGlass, Plus, X, Sparkle, UploadSimple, CheckCircle, ArrowClockwise,
  Trash, ArrowRight, Warning, Robot, Books, CaretUp, CaretDown,
} from "@phosphor-icons/react";

const TEMPLATE_LABEL = {
  direct: "Direct",
  context: "Context",
  deduction: "Deduction",
  story: "Story",
  compare: "Compare",
  timeline: "Timeline",
};

export default function AdminNKE() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  // Cross-tab payloads:
  //   composerPrefill: { fact_ids: [...] } jumps user to composer with these facts selected
  const [composerPrefill, setComposerPrefill] = useState(null);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/admin/nke/stats");
      setStats(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load stats"); }
  };
  useEffect(() => { loadStats(); }, []);

  const jumpToComposer = (fact_ids) => {
    setComposerPrefill({ fact_ids });
    setTab("composer");
  };

  return (
    <div className="space-y-6" data-testid="admin-nke-root">
      <header className="hidden lg:flex items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] text-white/40 uppercase tracking-[0.18em]">Command Center</p>
          <h1 className="font-display text-3xl font-medium tracking-tight">NK Knowledge Engine</h1>
          <p className="text-sm text-white/60 mt-2">
            Store structured knowledge. Compose diverse question variants. Serve them without repeats.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-white/60">
          <span data-testid="nke-stat-facts">
            <span className="text-nk-gold font-medium">{stats?.facts?.total ?? "—"}</span> facts
          </span>
          <span data-testid="nke-stat-variants">
            <span className="text-nk-gold font-medium">{stats?.variants?.total ?? "—"}</span> variants
          </span>
          <span data-testid="nke-stat-live">
            <span className="text-nk-gold font-medium">
              {stats?.variants?.by_status?.live ?? 0}
            </span> live
          </span>
        </div>
      </header>

      <div className="lg:hidden">
        <p className="font-mono text-[10px] text-white/40 uppercase tracking-[0.18em]">Command Center</p>
        <h1 className="font-display text-2xl font-medium">NK Knowledge Engine</h1>
      </div>

      <nav className="flex gap-1 border-b border-nk-line" data-testid="admin-nke-tabs">
        {[
          { key: "overview", label: "Overview" },
          { key: "facts", label: "Facts" },
          { key: "composer", label: "Composer" },
          { key: "variants", label: "Variants" },
          { key: "coverage", label: "Coverage" },
          { key: "migrate", label: "Migrate" },
          { key: "tuning", label: "Tuning" },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`admin-nke-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-nk-gold text-nk-text"
                : "border-transparent text-white/60 hover:text-nk-text"
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "overview" && <OverviewTab stats={stats} reload={loadStats} />}
      {tab === "facts"    && <FactsTab reload={loadStats} />}
      {tab === "composer" && <ComposerTab reload={loadStats} prefill={composerPrefill} onConsumePrefill={() => setComposerPrefill(null)} />}
      {tab === "variants" && <VariantsTab reload={loadStats} />}
      {tab === "coverage" && <CoverageTab onFillGaps={jumpToComposer} />}
      {tab === "migrate"  && <MigrateTab reload={loadStats} onCompose={jumpToComposer} />}
      {tab === "tuning"   && <TuningTab />}
    </div>
  );
}

// ---------------------------------------------------------------- Overview
function OverviewTab({ stats, reload }) {
  const [lastJob, setLastJob] = useState(null);
  const [weeklyTuned, setWeeklyTuned] = useState(null);
  useEffect(() => {
    if (stats?.last_compose_job) setLastJob(stats.last_compose_job);
  }, [stats]);
  useEffect(() => {
    api.get("/admin/nke/tuning/weekly-count")
      .then((r) => setWeeklyTuned(r.data.promoted_last_7d))
      .catch(() => {});
  }, []);

  const seedStarter = async () => {
    try {
      const { data } = await api.post("/admin/nke/facts/seed");
      toast.success(`Seeded ${data.inserted} facts (skipped ${data.skipped})`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Seed failed"); }
  };

  if (!stats) return <div className="text-sm text-white/60">Loading…</div>;

  const byStatus = stats.variants?.by_status || {};
  const validation = stats.variants?.validation || {};

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total facts"     value={stats.facts?.total ?? 0} testid="nke-card-facts-total" />
        <StatCard label="Active facts"    value={stats.facts?.active ?? 0} testid="nke-card-facts-active" />
        <StatCard label="Variants live"   value={byStatus.live ?? 0}       accent testid="nke-card-variants-live" />
        <StatCard label="Awaiting review" value={byStatus.pending_review ?? 0} testid="nke-card-variants-pending" />
      </div>

      {weeklyTuned != null && (
        <div className="flex items-center gap-2 text-xs" data-testid="nke-weekly-tuned">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-nk-gold/40 rounded-sm bg-nk-gold/[0.06]">
            <ArrowClockwise size={12} className="text-nk-gold" />
            <span className="text-white/60">Auto-tuned this week:</span>
            <b className="text-nk-gold">{weeklyTuned}</b>
            <span className="text-white/60">variant{weeklyTuned === 1 ? "" : "s"}</span>
          </span>
        </div>
      )}

      <section className="border border-nk-line rounded-sm p-4 space-y-3">
        <h2 className="font-display text-lg">Validation pass rate</h2>
        <div className="text-sm text-white/70 flex flex-wrap gap-4">
          <span>Validated: <b className="text-nk-text">{validation.validated ?? 0}</b></span>
          <span>Passed: <b className="text-emerald-400">{validation.passed ?? 0}</b></span>
          <span>Pending validation: <b className="text-yellow-400">{validation.pending ?? 0}</b></span>
          {validation.pass_rate_pct != null && (
            <span>Pass rate: <b className="text-nk-gold">{validation.pass_rate_pct}%</b></span>
          )}
        </div>
      </section>

      <section className="border border-nk-line rounded-sm p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg">Fact library by category</h2>
          <button
            data-testid="nke-seed-starter-btn"
            onClick={seedStarter}
            className="text-xs px-3 py-1.5 border border-nk-line rounded-sm hover:border-nk-gold hover:text-nk-gold"
          >
            Seed starter facts
          </button>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {Object.entries(stats.facts?.by_category || {}).map(([cat, n]) => (
            <span key={cat} className="px-2 py-1 border border-nk-line rounded-sm">
              {cat}: <b className="text-nk-gold">{n}</b>
            </span>
          ))}
        </div>
      </section>

      <section className="border border-nk-line rounded-sm p-4">
        <h2 className="font-display text-lg mb-2">Last compose job</h2>
        {lastJob ? (
          <div className="text-sm space-y-1">
            <div>Status: <b className={lastJob.status === "done" ? "text-emerald-400" : "text-yellow-400"}>{lastJob.status}</b></div>
            <div>Variants created: <b>{lastJob.progress?.variants_created ?? 0}</b> / {lastJob.progress?.total ?? 0}</div>
            <div className="text-white/50 text-xs">Job ID: {lastJob.job_id}</div>
            {lastJob.error && <div className="text-red-400 text-xs">{lastJob.error}</div>}
          </div>
        ) : (
          <div className="text-sm text-white/60">No jobs yet. Head to the Composer tab to generate variants.</div>
        )}
      </section>
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div className="border border-nk-line rounded-sm p-3" data-testid={testid}>
      <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">{label}</div>
      <div className={`font-display text-3xl mt-1 ${accent ? "text-nk-gold" : "text-nk-text"}`}>
        {value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Facts
function FactsTab({ reload }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [status, setStatus] = useState("active");
  const [facts, setFacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState(null); // "new" or fact obj
  const [busy, setBusy] = useState(false);
  const csvRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/nke/facts", {
        params: { q: q || undefined, category: cat || undefined, status: status || undefined, limit: 100 },
      });
      setFacts(data.facts || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const save = async (payload) => {
    try {
      if (payload.fact_id) {
        await api.patch(`/admin/nke/facts/${payload.fact_id}`, payload);
        toast.success("Fact updated");
      } else {
        await api.post("/admin/nke/facts", payload);
        toast.success("Fact created");
      }
      setEditing(null); load(); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const archive = async (fid) => {
    if (!confirm("Archive this fact? It will stop feeding the composer.")) return;
    try {
      await api.delete(`/admin/nke/facts/${fid}`);
      toast.success("Archived");
      load(); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const uploadCsv = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/nke/facts/import-csv", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.inserted} facts (skipped ${data.skipped}${data.errors_count ? `, errors ${data.errors_count}` : ""})`);
      load(); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "CSV import failed"); }
    finally { setBusy(false); if (csvRef.current) csvRef.current.value = ""; }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-2 bg-nk-bg border border-nk-line rounded-sm px-3 py-2 flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="text-white/60" />
          <input
            data-testid="nke-facts-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search subject / object / notes…"
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)}
                className="bg-nk-bg border border-nk-line px-3 py-2 rounded-sm text-sm"
                data-testid="nke-facts-cat">
          <option value="">All categories</option>
          {["History","Geography","Science","Sports","Music","Literature","Art","Politics","Economics","Technology"].map((c) =>
            <option key={c} value={c}>{c}</option>
          )}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="bg-nk-bg border border-nk-line px-3 py-2 rounded-sm text-sm"
                data-testid="nke-facts-status">
          <option value="active">Active</option>
          <option value="archived">Archived</option>
          <option value="">Any status</option>
        </select>
        <input ref={csvRef} type="file" accept=".csv,text/csv"
               onChange={(e) => uploadCsv(e.target.files?.[0])} className="hidden" />
        <button
          disabled={busy}
          onClick={() => csvRef.current?.click()}
          data-testid="nke-facts-csv-btn"
          className="px-3 py-2 border border-nk-line rounded-sm text-sm inline-flex items-center gap-2 hover:border-nk-gold"
        >
          <UploadSimple size={14} /> {busy ? "Uploading…" : "Import CSV"}
        </button>
        <button
          onClick={() => setEditing("new")}
          data-testid="nke-facts-new-btn"
          className="px-3 py-2 bg-nk-gold text-nk-bg text-sm font-medium rounded-sm inline-flex items-center gap-2 hover:bg-nk-accentSoft"
        >
          <Plus size={14} weight="bold" /> New fact
        </button>
      </div>

      <div className="text-xs text-white/50" data-testid="nke-facts-total">Showing {facts.length} of {total} facts</div>

      <div className="grid gap-2">
        {facts.map((f) => (
          <div key={f.fact_id}
               className="border border-nk-line rounded-sm p-3 hover:border-nk-gold/40 cursor-pointer group"
               onClick={() => setEditing(f)}
               data-testid={`nke-fact-row-${f.fact_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  <b className="text-nk-text">{f.subject}</b>
                  <span className="text-white/50"> {f.predicate} </span>
                  <b className="text-nk-gold">{f.object}</b>
                </div>
                <div className="flex flex-wrap gap-2 mt-1 text-[10px] text-white/50">
                  <span className="px-1.5 border border-nk-line rounded-sm">{f.category}</span>
                  {f.era && <span className="px-1.5 border border-nk-line rounded-sm">{f.era}</span>}
                  {f.region && <span className="px-1.5 border border-nk-line rounded-sm">{f.region}</span>}
                  <span className="px-1.5 border border-nk-line rounded-sm capitalize">{f.difficulty_seed}</span>
                  {(f.tags || []).slice(0, 3).map((t) => (
                    <span key={t} className="px-1.5 border border-nk-line rounded-sm text-white/40">#{t}</span>
                  ))}
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); archive(f.fact_id); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-white/40 hover:text-red-400"
                title="Archive"
                data-testid={`nke-fact-archive-${f.fact_id}`}
              >
                <Trash size={14} />
              </button>
            </div>
          </div>
        ))}
        {facts.length === 0 && (
          <div className="text-sm text-white/60 border border-dashed border-nk-line rounded-sm p-6 text-center">
            No facts. Click <b>New fact</b> or run <b>Seed starter facts</b> from the Overview tab.
          </div>
        )}
      </div>

      {editing && (
        <FactEditor
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function FactEditor({ initial, onClose, onSave }) {
  const [f, setF] = useState(() => initial || {
    subject: "", predicate: "", object: "", category: "General",
    era: "", region: "", difficulty_seed: "medium",
    tags: [], notes: "", country_tag: "ALL",
    // Rich Knowledge Object fields (Phase 1)
    entity: "", entity_type: "",
    categories: [], subcategories: [],
    facts: [], related_entities: [],
    sources: [], verified: false,
  });
  const [tagInput, setTagInput] = useState("");
  const [factInput, setFactInput] = useState("");
  const [relInput, setRelInput] = useState("");
  const [taxonomy, setTaxonomy] = useState({ categories: [] });
  const [showAdvanced, setShowAdvanced] = useState(!!initial?.entity);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    api.get("/admin/nke/taxonomy").then((r) => setTaxonomy(r.data)).catch(() => {});
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggleCat = (c) => setF((cur) => ({
    ...cur,
    categories: cur.categories?.includes(c)
      ? cur.categories.filter((x) => x !== c)
      : [...(cur.categories || []), c],
  }));
  const toggleSub = (s) => setF((cur) => ({
    ...cur,
    subcategories: cur.subcategories?.includes(s)
      ? cur.subcategories.filter((x) => x !== s)
      : [...(cur.subcategories || []), s],
  }));
  const availableSubs = (taxonomy.categories || [])
    .filter((c) => (f.categories || []).includes(c.key))
    .flatMap((c) => c.subs);

  const addFactLine = () => {
    const t = factInput.trim();
    if (!t) return;
    setF({ ...f, facts: [...(f.facts || []), t].slice(0, 25) });
    setFactInput("");
  };
  const addRel = () => {
    const t = relInput.trim();
    if (!t) return;
    if (!(f.related_entities || []).includes(t)) {
      setF({ ...f, related_entities: [...(f.related_entities || []), t].slice(0, 25) });
    }
    setRelInput("");
  };
  const addSource = () => setF({
    ...f, sources: [...(f.sources || []), { label: "", tier: 3, url: "" }],
  });
  const patchSource = (i, patch) => {
    const cp = [...(f.sources || [])];
    cp[i] = { ...cp[i], ...patch };
    setF({ ...f, sources: cp });
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!(f.tags || []).includes(t)) setF({ ...f, tags: [...(f.tags || []), t] });
    setTagInput("");
  };

  const commit = () => {
    if (!f.subject.trim() || !f.predicate.trim() || !f.object.trim()) {
      toast.error("subject, predicate, object are required");
      return;
    }
    onSave(f);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-nk-bg border border-nk-line rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-4"
           onClick={(e) => e.stopPropagation()}
           data-testid="nke-fact-editor">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl">{initial ? "Edit fact" : "New fact"}</h2>
          <button onClick={onClose} className="p-1 text-white/60 hover:text-nk-text"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Subject *">
            <input data-testid="nke-fact-subject"
                   value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })}
                   className="input" placeholder="e.g. Kwame Nkrumah" />
          </Field>
          <Field label="Predicate *">
            <input data-testid="nke-fact-predicate"
                   value={f.predicate} onChange={(e) => setF({ ...f, predicate: e.target.value })}
                   className="input" placeholder="e.g. was the first president of" />
          </Field>
          <Field label="Object *">
            <input data-testid="nke-fact-object"
                   value={f.object} onChange={(e) => setF({ ...f, object: e.target.value })}
                   className="input" placeholder="e.g. Ghana" />
          </Field>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Category">
            <input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="input" />
          </Field>
          <Field label="Era">
            <input value={f.era || ""} onChange={(e) => setF({ ...f, era: e.target.value })} className="input" placeholder="20th century" />
          </Field>
          <Field label="Region">
            <input value={f.region || ""} onChange={(e) => setF({ ...f, region: e.target.value })} className="input" placeholder="West Africa" />
          </Field>
          <Field label="Difficulty seed">
            <select value={f.difficulty_seed} onChange={(e) => setF({ ...f, difficulty_seed: e.target.value })} className="input capitalize">
              {["easy","medium","hard"].map((d) => <option key={d}>{d}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Tags">
          <div className="flex items-center gap-2">
            <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                   placeholder="type + Enter"
                   className="input flex-1" />
            <button onClick={addTag} className="px-3 py-1.5 border border-nk-line rounded-sm text-xs">Add</button>
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {(f.tags || []).map((t) => (
              <span key={t} className="text-[10px] px-2 py-0.5 border border-nk-line rounded-sm">
                #{t}
                <button className="ml-1 text-white/40 hover:text-red-400"
                        onClick={() => setF({ ...f, tags: f.tags.filter((x) => x !== t) })}>×</button>
              </span>
            ))}
          </div>
        </Field>

        <Field label="Notes">
          <textarea value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })}
                    rows={3} className="input" placeholder="Extra context for the AI composer…" />
        </Field>

        {/* Knowledge Object (Phase 1) — collapsible advanced section */}
        <button type="button" onClick={() => setShowAdvanced((v) => !v)}
                className="text-xs text-nk-gold inline-flex items-center gap-1 mt-2"
                data-testid="nke-fact-toggle-advanced">
          {showAdvanced ? <CaretUp size={12} /> : <CaretDown size={12} />}
          Knowledge Object · rich taxonomy + facts + sources
        </button>

        {showAdvanced && (
          <div className="space-y-3 border-t border-nk-line pt-3" data-testid="nke-fact-advanced">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Entity name">
                <input value={f.entity || ""} onChange={(e) => setF({ ...f, entity: e.target.value })}
                       className="input" placeholder="Accra (defaults to Subject)" />
              </Field>
              <Field label="Entity type">
                <input value={f.entity_type || ""} onChange={(e) => setF({ ...f, entity_type: e.target.value })}
                       className="input" placeholder="City / Person / Event / Country / Concept" />
              </Field>
            </div>

            <Field label="Categories (multi-select)">
              <div className="flex flex-wrap gap-1.5">
                {(taxonomy.categories || []).map((c) => (
                  <button key={c.key} type="button" onClick={() => toggleCat(c.key)}
                          data-testid={`nke-fact-cat-${c.key.toLowerCase().replace(/\s+/g,'-')}`}
                          className={`px-2 py-1 text-[11px] border rounded-sm ${
                            (f.categories || []).includes(c.key)
                              ? "border-nk-gold text-nk-gold bg-nk-gold/[0.06]"
                              : "border-nk-line text-white/60 hover:text-white"
                          }`}>
                    {c.emoji} {c.key}
                  </button>
                ))}
              </div>
            </Field>

            {availableSubs.length > 0 && (
              <Field label={`Subcategories (from ${(f.categories||[]).length} selected)`}>
                <div className="flex flex-wrap gap-1">
                  {availableSubs.map((s) => (
                    <button key={s} type="button" onClick={() => toggleSub(s)}
                            className={`px-2 py-0.5 text-[10px] border rounded-sm ${
                              (f.subcategories || []).includes(s)
                                ? "border-nk-gold text-nk-gold bg-nk-gold/[0.06]"
                                : "border-nk-line text-white/50 hover:text-white/80"
                            }`}>
                      {s}
                    </button>
                  ))}
                </div>
              </Field>
            )}

            <Field label={`Facts (${(f.facts || []).length}/25) — atomic statements about the entity`}>
              <div className="flex items-center gap-2">
                <input value={factInput} onChange={(e) => setFactInput(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFactLine())}
                       placeholder="e.g. Capital of Ghana"
                       className="input flex-1" data-testid="nke-fact-facts-input" />
                <button type="button" onClick={addFactLine} className="px-3 py-1.5 border border-nk-line rounded-sm text-xs">Add</button>
              </div>
              <ul className="space-y-1 mt-2">
                {(f.facts || []).map((line, i) => (
                  <li key={i} className="flex items-center justify-between text-xs px-2 py-1 border border-nk-line/40 rounded-sm">
                    <span className="text-white/80">{line}</span>
                    <button type="button" className="text-white/40 hover:text-red-400"
                            onClick={() => setF({ ...f, facts: f.facts.filter((_, j) => j !== i) })}>×</button>
                  </li>
                ))}
              </ul>
            </Field>

            <Field label="Related entities (foundation for Knowledge Graph)">
              <div className="flex items-center gap-2">
                <input value={relInput} onChange={(e) => setRelInput(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRel())}
                       placeholder="e.g. Kwame Nkrumah"
                       className="input flex-1" data-testid="nke-fact-related-input" />
                <button type="button" onClick={addRel} className="px-3 py-1.5 border border-nk-line rounded-sm text-xs">Add</button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {(f.related_entities || []).map((t) => (
                  <span key={t} className="text-[10px] px-2 py-0.5 border border-nk-line rounded-sm">
                    {t}
                    <button type="button" className="ml-1 text-white/40 hover:text-red-400"
                            onClick={() => setF({ ...f, related_entities: f.related_entities.filter((x) => x !== t) })}>×</button>
                  </span>
                ))}
              </div>
            </Field>

            <Field label="Sources (tier drives the confidence score)">
              <div className="space-y-1.5">
                {(f.sources || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <select value={s.tier || 3} onChange={(e) => patchSource(i, { tier: Number(e.target.value) })}
                            className="input" style={{ width: "80px" }}>
                      {[1, 2, 3, 4].map((t) => <option key={t} value={t}>T{t}</option>)}
                    </select>
                    <input value={s.label || ""} onChange={(e) => patchSource(i, { label: e.target.value })}
                           placeholder="e.g. CIA World Factbook" className="input flex-1" />
                    <input value={s.url || ""} onChange={(e) => patchSource(i, { url: e.target.value })}
                           placeholder="url (optional)" className="input flex-1" />
                    <button type="button" className="text-white/40 hover:text-red-400"
                            onClick={() => setF({ ...f, sources: f.sources.filter((_, j) => j !== i) })}>×</button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addSource}
                      className="mt-2 px-3 py-1 border border-nk-line rounded-sm text-[11px]">+ Add source</button>
              <p className="text-[10px] text-white/40 mt-1">
                T1 authoritative (0.98) · T2 editorial (0.90) · T3 internal (0.85) · T4 AI-assist (0.55)
              </p>
            </Field>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={f.verified || false}
                     onChange={(e) => setF({ ...f, verified: e.target.checked })}
                     data-testid="nke-fact-verified" />
              <span>Verified by an editor</span>
            </label>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-nk-line rounded-sm">Cancel</button>
          <button data-testid="nke-fact-save-btn"
                  onClick={commit}
                  className="px-4 py-2 bg-nk-gold text-nk-bg text-sm font-medium rounded-sm inline-flex items-center gap-2">
            <CheckCircle size={14} weight="bold" /> Save
          </button>
        </div>
      </div>
      <style>{`.input{background:transparent;border:1px solid rgba(255,255,255,0.1);padding:0.5rem 0.75rem;border-radius:2px;font-size:0.875rem;width:100%;outline:none;} .input:focus{border-color:#d4af37;}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">{label}</div>
      {children}
    </label>
  );
}

// ---------------------------------------------------------------- Composer
function ComposerTab({ reload, prefill, onConsumePrefill }) {
  const [templates, setTemplates] = useState(Object.keys(TEMPLATE_LABEL));
  const [limit, setLimit] = useState(5);
  const [factIds, setFactIds] = useState(""); // comma-separated
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  // Consume prefill from Coverage/Migrate tabs.
  useEffect(() => {
    if (prefill?.fact_ids?.length) {
      setFactIds(prefill.fact_ids.join(", "));
      toast.info(`${prefill.fact_ids.length} fact${prefill.fact_ids.length === 1 ? "" : "s"} pre-selected — pick templates & Start.`);
      onConsumePrefill && onConsumePrefill();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const toggleTemplate = (t) => setTemplates((cur) =>
    cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]
  );

  const start = async () => {
    if (templates.length === 0) { toast.error("Pick at least one template"); return; }
    setBusy(true);
    try {
      const body = { templates };
      if (factIds.trim()) body.fact_ids = factIds.split(",").map((s) => s.trim()).filter(Boolean);
      else body.limit = Math.max(1, Math.min(100, Number(limit) || 5));
      const { data } = await api.post("/admin/nke/compose", body);
      toast.success(`Composer job ${data.job_id.slice(0,12)}… queued (${data.total_variants} variants)`);
      pollJob(data.job_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Composer failed to start"); }
    finally { setBusy(false); }
  };

  const pollJob = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const { data } = await api.get(`/admin/nke/compose/${jobId}`);
        setJob(data);
        if (["done", "failed", "partial"].includes(data.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          reload();
        }
      } catch (e) { /* keep polling */ }
    };
    tick();
    pollRef.current = setInterval(tick, 4000);
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const pct = useMemo(() => {
    if (!job?.progress?.total) return 0;
    return Math.round(100 * (job.progress.done || 0) / job.progress.total);
  }, [job]);

  return (
    <div className="space-y-6">
      <section className="border border-nk-line rounded-sm p-4 space-y-4">
        <h2 className="font-display text-lg flex items-center gap-2"><Sparkle size={16} weight="fill" /> Generate variants</h2>

        <Field label="Templates (6 available)">
          <div className="flex flex-wrap gap-2">
            {Object.entries(TEMPLATE_LABEL).map(([k, lbl]) => (
              <button key={k}
                      data-testid={`nke-composer-tmpl-${k}`}
                      onClick={() => toggleTemplate(k)}
                      className={`px-3 py-1 text-xs border rounded-sm ${
                        templates.includes(k) ? "border-nk-gold text-nk-gold" : "border-nk-line text-white/60"
                      }`}>
                {lbl}
              </button>
            ))}
          </div>
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Auto-pick N facts (least-covered first)">
            <input type="number" value={limit} min={1} max={100}
                   onChange={(e) => setLimit(e.target.value)}
                   data-testid="nke-composer-limit"
                   className="input" />
          </Field>
          <Field label="… OR paste explicit fact IDs (comma-separated)">
            <input value={factIds} onChange={(e) => setFactIds(e.target.value)}
                   data-testid="nke-composer-fact-ids"
                   placeholder="fact_abc, fact_def"
                   className="input" />
          </Field>
        </div>

        <div className="flex justify-end">
          <button onClick={start} disabled={busy || (job && job.status === "running")}
                  data-testid="nke-composer-start"
                  className="px-4 py-2 bg-nk-gold text-nk-bg text-sm font-medium rounded-sm inline-flex items-center gap-2 disabled:opacity-40">
            <Sparkle size={14} weight="fill" /> {busy ? "Starting…" : "Start compose job"}
          </button>
        </div>
      </section>

      {job && (
        <section className="border border-nk-line rounded-sm p-4 space-y-3" data-testid="nke-composer-status">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-base">Job {job.job_id.slice(0, 16)}…</h3>
            <span className={`text-xs px-2 py-1 border rounded-sm ${
              job.status === "done" ? "border-emerald-400 text-emerald-400" :
              job.status === "failed" ? "border-red-400 text-red-400" :
              job.status === "partial" ? "border-yellow-400 text-yellow-400" :
              "border-nk-gold text-nk-gold"
            }`}>{job.status}</span>
          </div>
          <div className="h-2 bg-nk-line rounded-sm overflow-hidden">
            <div className="h-full bg-nk-gold transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-xs text-white/60">
            {job.progress?.done ?? 0} / {job.progress?.total ?? 0} steps ·
            {" "}{job.progress?.variants_created ?? 0} variants created ·
            {" "}{job.progress?.failed ?? 0} failed
          </div>
          {job.error && (
            <div className="text-xs text-red-400 flex items-start gap-2">
              <Warning size={14} /> {job.error}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Variants
function VariantsTab({ reload }) {
  const [status, setStatus] = useState("pending_review");
  const [tmpl, setTmpl] = useState("");
  const [q, setQ] = useState("");
  const [variants, setVariants] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [validating, setValidating] = useState(null);
  const [bulking, setBulking] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/nke/variants", {
        params: { status: status || undefined, template_type: tmpl || undefined, q: q || undefined, limit: 100 },
      });
      setVariants(data.variants || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, tmpl]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const patch = async (vid, patchObj) => {
    try {
      await api.patch(`/admin/nke/variants/${vid}`, patchObj);
      toast.success("Updated");
      load(); reload();
      if (selected?.variant_id === vid) {
        const { data } = await api.get(`/admin/nke/variants/${vid}`);
        setSelected(data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const revalidate = async (vid) => {
    setValidating(vid);
    try {
      const { data } = await api.post(`/admin/nke/variants/${vid}/validate`);
      toast.success(`Validated — ${data.validation.all_pass ? "all pass ✅" : "review needed"}`);
      load();
      if (selected?.variant_id === vid) setSelected({ ...selected, validation: data.validation });
    } catch (e) { toast.error(e.response?.data?.detail || "Validate failed"); }
    finally { setValidating(null); }
  };

  const bulkValidate = async () => {
    if (bulking) return;
    setBulking(true);
    try {
      const { data } = await api.post("/admin/nke/variants/bulk-validate", {
        status: "pending_review", limit: 25, only_missing: true, run_llm: true,
      });
      toast.success(`Scanned ${data.scanned}: ${data.passed} passed · ${data.failed_gate} flagged`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk validate failed"); }
    finally { setBulking(false); }
  };

  const openDetail = async (vid) => {
    try {
      const { data } = await api.get(`/admin/nke/variants/${vid}`);
      setSelected(data);
    } catch (e) { toast.error("Failed to load variant"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-2 bg-nk-bg border border-nk-line rounded-sm px-3 py-2 flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="text-white/60" />
          <input data-testid="nke-variants-search"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search question body…"
                 className="flex-1 bg-transparent outline-none text-sm" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                data-testid="nke-variants-status"
                className="bg-nk-bg border border-nk-line px-3 py-2 rounded-sm text-sm">
          <option value="pending_review">Pending review</option>
          <option value="approved">Approved</option>
          <option value="live">Live</option>
          <option value="rejected">Rejected</option>
          <option value="archived">Archived</option>
          <option value="">Any status</option>
        </select>
        <select value={tmpl} onChange={(e) => setTmpl(e.target.value)}
                data-testid="nke-variants-tmpl"
                className="bg-nk-bg border border-nk-line px-3 py-2 rounded-sm text-sm">
          <option value="">All templates</option>
          {Object.entries(TEMPLATE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button onClick={bulkValidate} disabled={bulking}
                data-testid="nke-variants-bulk-validate"
                className="px-3 py-2 border border-nk-line rounded-sm text-sm inline-flex items-center gap-2 hover:border-nk-gold disabled:opacity-40">
          <Robot size={14} /> {bulking ? "Validating…" : "Bulk validate"}
        </button>
      </div>

      <div className="text-xs text-white/50">Showing {variants.length} of {total}</div>

      <div className="grid gap-2">
        {variants.map((v) => (
          <div key={v.variant_id}
               onClick={() => openDetail(v.variant_id)}
               className="border border-nk-line rounded-sm p-3 hover:border-nk-gold/40 cursor-pointer"
               data-testid={`nke-variant-row-${v.variant_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[10px] mb-1">
                  <span className="px-1.5 py-0.5 border border-nk-line rounded-sm">{TEMPLATE_LABEL[v.template_type] || v.template_type}</span>
                  <span className="px-1.5 py-0.5 border border-nk-line rounded-sm capitalize">{v.difficulty}</span>
                  <StatusBadge status={v.status} />
                  <ValidationBadge v={v.validation} />
                </div>
                <div className="text-sm text-nk-text">{v.body}</div>
              </div>
              <ArrowRight size={16} className="text-white/30 shrink-0 mt-1" />
            </div>
          </div>
        ))}
        {variants.length === 0 && (
          <div className="text-sm text-white/60 border border-dashed border-nk-line rounded-sm p-6 text-center">
            No variants match. Run the Composer to generate some.
          </div>
        )}
      </div>

      {selected && (
        <VariantDetail
          v={selected}
          onClose={() => setSelected(null)}
          onPatch={patch}
          onRevalidate={revalidate}
          revalidating={validating === selected.variant_id}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending_review: "border-yellow-400 text-yellow-400",
    approved: "border-emerald-400 text-emerald-400",
    live: "border-nk-gold text-nk-gold",
    rejected: "border-red-400 text-red-400",
    archived: "border-white/30 text-white/40",
  };
  return <span className={`px-1.5 py-0.5 border rounded-sm ${map[status] || "border-nk-line"}`}>{status}</span>;
}

function ValidationBadge({ v }) {
  if (!v) return <span className="px-1.5 py-0.5 border border-white/20 text-white/40 rounded-sm">unvalidated</span>;
  if (v.all_pass) return <span className="px-1.5 py-0.5 border border-emerald-400 text-emerald-400 rounded-sm">✓ validated</span>;
  return <span className="px-1.5 py-0.5 border border-red-400 text-red-400 rounded-sm">gates failed</span>;
}

function VariantDetail({ v, onClose, onPatch, onRevalidate, revalidating }) {
  const [edit, setEdit] = useState(false);
  const [body, setBody] = useState(v.body);
  const [options, setOptions] = useState([...v.options]);
  const [answerIdx, setAnswerIdx] = useState(v.answer_index);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const saveEdit = () => {
    if (options.filter(Boolean).length !== 4) { toast.error("Need 4 options"); return; }
    onPatch(v.variant_id, { body, options, answer_index: Number(answerIdx) });
    setEdit(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-nk-bg border border-nk-line rounded-sm w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6 space-y-4"
           onClick={(e) => e.stopPropagation()}
           data-testid="nke-variant-detail">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
              {TEMPLATE_LABEL[v.template_type] || v.template_type} · {v.difficulty}
            </p>
            <h2 className="font-display text-xl">Variant review</h2>
          </div>
          <button onClick={onClose} className="p-1 text-white/60 hover:text-nk-text"><X size={18} /></button>
        </div>

        {v.fact && (
          <div className="border border-nk-line rounded-sm p-3 text-sm bg-white/[0.02]">
            <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mb-1 flex items-center gap-1"><Books size={12} /> Source fact</div>
            <div>
              <b className="text-nk-text">{v.fact.subject}</b>
              <span className="text-white/50"> {v.fact.predicate} </span>
              <b className="text-nk-gold">{v.fact.object}</b>
            </div>
            {v.fact.notes && <div className="text-white/60 mt-1 text-xs">{v.fact.notes}</div>}
          </div>
        )}

        {!edit ? (
          <>
            <div className="text-base text-nk-text leading-relaxed" data-testid="nke-variant-body">{v.body}</div>
            <div className="space-y-2">
              {v.options.map((o, i) => (
                <div key={i}
                     className={`px-3 py-2 border rounded-sm text-sm ${
                       i === v.answer_index ? "border-emerald-400 text-emerald-400" : "border-nk-line text-white/80"
                     }`}>
                  {String.fromCharCode(65 + i)}. {o} {i === v.answer_index && "✓"}
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4}
                      className="w-full bg-transparent border border-nk-line rounded-sm p-2 text-sm" />
            {options.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="radio" checked={answerIdx === i} onChange={() => setAnswerIdx(i)} />
                <input value={o} onChange={(e) => { const cp = [...options]; cp[i] = e.target.value; setOptions(cp); }}
                       className="flex-1 bg-transparent border border-nk-line rounded-sm p-2 text-sm" />
              </div>
            ))}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEdit(false)} className="px-3 py-1.5 border border-nk-line rounded-sm text-sm">Cancel</button>
              <button onClick={saveEdit} className="px-3 py-1.5 bg-nk-gold text-nk-bg rounded-sm text-sm">Save edits</button>
            </div>
          </>
        )}

        {v.validation && (
          <div className="border border-nk-line rounded-sm p-3 text-xs space-y-1" data-testid="nke-variant-validation">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">Validation</div>
            <ValidationRow label="Single correct" v={v.validation.single_correct} />
            <ValidationRow label="Clarity"        v={v.validation.clarity} />
            <ValidationRow label="Semantic dedupe" v={v.validation.dedupe} />
            {v.validation.difficulty_recommendation && (
              <div>Difficulty recommendation: <b className="text-nk-gold">{v.validation.difficulty_recommendation}</b></div>
            )}
            {(v.validation.notes || []).length > 0 && (
              <ul className="list-disc list-inside text-white/50 mt-1">
                {v.validation.notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2 border-t border-nk-line">
          <button data-testid="nke-variant-edit-btn"
                  onClick={() => setEdit(!edit)}
                  className="px-3 py-2 border border-nk-line rounded-sm text-sm">
            {edit ? "Cancel edit" : "Edit"}
          </button>
          <button data-testid="nke-variant-revalidate"
                  onClick={() => onRevalidate(v.variant_id)}
                  disabled={revalidating}
                  className="px-3 py-2 border border-nk-line rounded-sm text-sm inline-flex items-center gap-2 disabled:opacity-40">
            <ArrowClockwise size={14} /> {revalidating ? "Validating…" : "Re-validate"}
          </button>
          <div className="flex-1" />
          {v.status !== "approved" && (
            <button data-testid="nke-variant-approve"
                    onClick={() => onPatch(v.variant_id, { status: "approved" })}
                    className="px-3 py-2 border border-emerald-400 text-emerald-400 rounded-sm text-sm">
              Approve
            </button>
          )}
          {v.status !== "live" && (
            <button data-testid="nke-variant-live"
                    onClick={() => onPatch(v.variant_id, { status: "live" })}
                    className="px-3 py-2 bg-nk-gold text-nk-bg rounded-sm text-sm font-medium">
              Set live
            </button>
          )}
          {v.status !== "rejected" && (
            <button data-testid="nke-variant-reject"
                    onClick={() => onPatch(v.variant_id, { status: "rejected" })}
                    className="px-3 py-2 border border-red-400 text-red-400 rounded-sm text-sm">
              Reject
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ValidationRow({ label, v }) {
  const color = v === "pass" ? "text-emerald-400" : v === "fail" ? "text-red-400" : "text-white/40";
  const glyph = v === "pass" ? "✓" : v === "fail" ? "✗" : "–";
  return (
    <div className="flex items-center gap-2">
      <span className="w-32 text-white/60">{label}</span>
      <span className={color}>{glyph} {v}</span>
    </div>
  );
}

// ---------------------------------------------------------------- Coverage
function CoverageTab({ onFillGaps }) {
  const [data, setData] = useState(null);
  const [limit, setLimit] = useState(20);
  const [order, setOrder] = useState("under_covered");
  const [cat, setCat] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/nke/coverage", {
        params: { limit, order, category: cat || undefined },
      });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [limit, order, cat]);

  const fillAllGaps = () => {
    if (!data) return;
    const targets = data.facts.filter((f) => f.filled_count < 6).map((f) => f.fact_id);
    if (targets.length === 0) { toast.info("All visible facts are already fully covered."); return; }
    onFillGaps && onFillGaps(targets);
  };

  if (!data) return <div className="text-sm text-white/60">Loading heatmap…</div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total facts"     value={data.summary.total}   testid="cov-total" />
        <StatCard label="Full coverage"   value={data.summary.full}    accent testid="cov-full" />
        <StatCard label="Partial"         value={data.summary.partial} testid="cov-partial" />
        <StatCard label="Zero variants"   value={data.summary.empty}   testid="cov-empty" />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <select value={order} onChange={(e) => setOrder(e.target.value)}
                data-testid="cov-order"
                className="bg-nk-bg border border-nk-line px-3 py-2 rounded-sm text-sm">
          <option value="under_covered">Under-covered first</option>
          <option value="full_covered">Fully covered first</option>
          <option value="by_created">Newest first</option>
          <option value="by_category">Group by category</option>
        </select>
        <select value={cat} onChange={(e) => setCat(e.target.value)}
                data-testid="cov-category"
                className="bg-nk-bg border border-nk-line px-3 py-2 rounded-sm text-sm">
          <option value="">All categories</option>
          {["History","Geography","Science","Sports","Music","Literature","Art","Politics","Economics","Technology"].map((c) =>
            <option key={c} value={c}>{c}</option>
          )}
        </select>
        <button
          onClick={() => setLimit(limit >= 500 ? 20 : 500)}
          data-testid="cov-view-all"
          className="px-3 py-2 border border-nk-line rounded-sm text-sm hover:border-nk-gold"
        >
          {limit >= 500 ? "Top 20 only" : "View all"}
        </button>
        <button
          onClick={fillAllGaps}
          data-testid="cov-fill-gaps"
          className="px-3 py-2 border border-nk-gold text-nk-gold rounded-sm text-sm hover:bg-nk-gold/10 inline-flex items-center gap-2"
          title="Send all under-covered facts to the composer"
        >
          <Sparkle size={12} weight="fill" /> Fill gaps in composer
        </button>
      </div>

      <div className="border border-nk-line rounded-sm overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-nk-line text-white/50">
              <th className="text-left px-3 py-2 font-mono uppercase tracking-widest text-[10px]">Fact</th>
              {data.templates.map((t) => (
                <th key={t} className="px-2 py-2 font-mono uppercase tracking-widest text-[10px]">{t}</th>
              ))}
              <th className="px-2 py-2 font-mono uppercase tracking-widest text-[10px]">Filled</th>
              <th className="px-2 py-2 font-mono uppercase tracking-widest text-[10px]"></th>
            </tr>
          </thead>
          <tbody>
            {data.facts.map((f) => (
              <tr key={f.fact_id}
                  className="border-b border-nk-line/50 hover:bg-white/[0.02] cursor-pointer"
                  onClick={() => onFillGaps && onFillGaps([f.fact_id])}
                  data-testid={`cov-row-${f.fact_id}`}
                  title="Click to compose missing templates for this fact">
                <td className="px-3 py-2">
                  <div className="text-nk-text truncate max-w-[400px]">
                    <b>{f.subject}</b> <span className="text-white/40 text-[10px]">{f.predicate}</span> {f.object}
                  </div>
                  <div className="text-[10px] text-white/40">{f.category}{f.region ? ` · ${f.region}` : ""}</div>
                </td>
                {data.templates.map((t) => (
                  <td key={t} className="px-2 py-2 text-center">
                    {f.has_template[t] ? (
                      <span className="inline-block w-6 h-6 bg-nk-gold/20 border border-nk-gold text-nk-gold rounded-sm leading-6">
                        {f.templates[t]}
                      </span>
                    ) : (
                      <span className="inline-block w-6 h-6 border border-white/10 text-white/20 rounded-sm leading-6">·</span>
                    )}
                  </td>
                ))}
                <td className="px-2 py-2 text-center">
                  <b className={f.filled_count >= 6 ? "text-emerald-400" : f.filled_count === 0 ? "text-red-400" : "text-yellow-400"}>
                    {f.filled_count}/6
                  </b>
                </td>
                <td className="px-2 py-2 text-center">
                  {f.filled_count < 6 && (
                    <span className="text-nk-gold text-[10px] opacity-0 group-hover:opacity-100 inline-flex items-center gap-1">
                      Compose <ArrowRight size={10} />
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {data.facts.length === 0 && (
              <tr><td colSpan={data.templates.length + 3} className="px-3 py-6 text-center text-white/60">No facts match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Migrate
function MigrateTab({ reload, onCompose }) {
  const [batchSize, setBatchSize] = useState(10);
  const [cat, setCat] = useState("");
  const [minUses, setMinUses] = useState(0);
  const [job, setJob] = useState(null);
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const loadRecent = async () => {
    try {
      const { data } = await api.get("/admin/nke/migrate/legacy", { params: { limit: 5 } });
      setRecent(data.jobs || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadRecent(); }, []);

  const start = async () => {
    setBusy(true);
    try {
      const body = { batch_size: Number(batchSize), min_uses: Number(minUses) };
      if (cat) body.category = cat;
      const { data } = await api.post("/admin/nke/migrate/legacy", body);
      toast.success(`Migration job ${data.job_id.slice(0, 12)}… queued`);
      pollJob(data.job_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start"); }
    finally { setBusy(false); }
  };

  const pollJob = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const { data } = await api.get(`/admin/nke/migrate/legacy/${jobId}`);
        setJob(data);
        if (["done", "failed", "partial"].includes(data.status)) {
          clearInterval(pollRef.current); pollRef.current = null;
          loadRecent(); reload();
        }
      } catch (e) { /* keep polling */ }
    };
    tick();
    pollRef.current = setInterval(tick, 4000);
  };
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const pct = useMemo(() => {
    if (!job?.progress?.total) return 0;
    return Math.round(100 * (job.progress.done || 0) / job.progress.total);
  }, [job]);

  return (
    <div className="space-y-6">
      <section className="border border-nk-line rounded-sm p-4 space-y-4">
        <div>
          <h2 className="font-display text-lg">Migrate legacy questions → knowledge facts</h2>
          <p className="text-xs text-white/60 mt-1">
            Reverse-engineers top-performing <code>question_bank</code> items into structured facts.
            The AI extracts <b>subject · predicate · object</b> triples and metadata.
            Original legacy questions stay live; they gain a <code>migrated_to_fact_id</code> link.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Batch size (max 25)">
            <input type="number" value={batchSize} min={1} max={25}
                   onChange={(e) => setBatchSize(e.target.value)}
                   data-testid="mig-batch-size"
                   className="input" />
          </Field>
          <Field label="Category filter (optional)">
            <select value={cat} onChange={(e) => setCat(e.target.value)}
                    data-testid="mig-category"
                    className="input">
              <option value="">Any category</option>
              {["History","Geography","Science","Sports","Music","Literature","Art","Politics","Economics","Technology"].map((c) =>
                <option key={c} value={c}>{c}</option>
              )}
            </select>
          </Field>
          <Field label="Minimum times used">
            <input type="number" value={minUses} min={0}
                   onChange={(e) => setMinUses(e.target.value)}
                   data-testid="mig-min-uses"
                   className="input" />
          </Field>
        </div>

        <div className="flex justify-end">
          <button onClick={start} disabled={busy || (job && job.status === "running")}
                  data-testid="mig-start"
                  className="px-4 py-2 bg-nk-gold text-nk-bg text-sm font-medium rounded-sm inline-flex items-center gap-2 disabled:opacity-40">
            <ArrowRight size={14} weight="bold" /> {busy ? "Starting…" : "Start migration"}
          </button>
        </div>
      </section>

      {job && (
        <section className="border border-nk-line rounded-sm p-4 space-y-3" data-testid="mig-job-status">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-base">Job {job.job_id.slice(0, 16)}…</h3>
            <span className={`text-xs px-2 py-1 border rounded-sm ${
              job.status === "done" ? "border-emerald-400 text-emerald-400" :
              job.status === "failed" ? "border-red-400 text-red-400" :
              job.status === "partial" ? "border-yellow-400 text-yellow-400" :
              "border-nk-gold text-nk-gold"
            }`}>{job.status}</span>
          </div>
          <div className="h-2 bg-nk-line rounded-sm overflow-hidden">
            <div className="h-full bg-nk-gold transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-xs text-white/60">
            {job.progress?.done ?? 0} / {job.progress?.total ?? 0} sources ·
            {" "}{job.progress?.facts_created ?? 0} new facts ·
            {" "}{job.progress?.skipped_dupes ?? 0} dupes ·
            {" "}{job.progress?.failed ?? 0} failed
          </div>
          {job.error && (
            <div className="text-xs text-red-400 flex items-start gap-2">
              <Warning size={14} /> {job.error}
            </div>
          )}
          {["done", "partial"].includes(job.status) && (job.facts_created_ids?.length > 0) && (
            <button
              onClick={() => onCompose && onCompose(job.facts_created_ids)}
              data-testid="mig-chain-compose"
              className="px-3 py-2 border border-nk-gold text-nk-gold rounded-sm text-sm inline-flex items-center gap-2 hover:bg-nk-gold/10"
            >
              <Sparkle size={12} weight="fill" />
              Compose variants for these {job.facts_created_ids.length} new facts
              <ArrowRight size={12} />
            </button>
          )}
        </section>
      )}

      {recent.length > 0 && (
        <section className="border border-nk-line rounded-sm p-4 space-y-2">
          <h3 className="font-display text-base">Recent migration jobs</h3>
          <div className="space-y-1 text-xs">
            {recent.map((r) => (
              <div key={r.job_id} className="flex items-center justify-between border-b border-nk-line/40 py-1"
                   data-testid={`mig-recent-${r.job_id}`}>
                <span className="font-mono text-white/50">{r.job_id.slice(0, 18)}…</span>
                <span className="text-white/60">{r.progress?.facts_created ?? 0} facts · {r.status}</span>
                <span className="text-white/40">{(r.completed_at || r.created_at || "").slice(0, 16)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Tuning
function TuningTab() {
  const [lastRun, setLastRun] = useState(null);
  const [changes, setChanges] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/nke/tuning/last-run"),
        api.get("/admin/nke/tuning/changes", { params: { limit: 25 } }),
      ]);
      setLastRun(a.data.last_run);
      setChanges(b.data.changes || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/nke/tuning/run-now");
      toast.success(`Scanned ${data.run.scanned} · promoted ${data.run.promoted} · pending ${data.run.pending_first_look}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <section className="border border-nk-line rounded-sm p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-lg">Difficulty auto-tuning</h2>
            <p className="text-xs text-white/60 mt-1 max-w-2xl">
              Nightly (every 24h) we scan variants with ≥20 answers and compute the ideal difficulty
              from live accuracy. A recommendation must be stable across <b>two consecutive runs</b>
              before we promote it (avoids flapping).
            </p>
          </div>
          <button onClick={runNow} disabled={busy}
                  data-testid="tuning-run-now"
                  className="px-4 py-2 bg-nk-gold text-nk-bg text-sm font-medium rounded-sm inline-flex items-center gap-2 disabled:opacity-40">
            <ArrowClockwise size={14} /> {busy ? "Tuning…" : "Run tuning now"}
          </button>
        </div>

        {lastRun ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <MiniStat label="Scanned"     value={lastRun.scanned} />
            <MiniStat label="Promoted"    value={lastRun.promoted} accent />
            <MiniStat label="Pending"     value={lastRun.pending_first_look} />
            <MiniStat label="Unchanged"   value={lastRun.unchanged} />
            <MiniStat label="Reset"       value={lastRun.reset} />
          </div>
        ) : (
          <div className="text-sm text-white/60">No runs yet — nightly cron starts 60s after backend boot.</div>
        )}
        {lastRun && (
          <div className="text-[11px] text-white/40 font-mono">
            {lastRun.run_id} · {lastRun.triggered_by} · {(lastRun.completed_at || "").slice(0, 19)}Z
          </div>
        )}
      </section>

      <section className="border border-nk-line rounded-sm p-4">
        <h3 className="font-display text-base mb-3">Recent difficulty changes</h3>
        {changes.length === 0 ? (
          <div className="text-sm text-white/60">
            No promotions yet — variants need ≥20 serves + a stable 2-run recommendation.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-nk-line text-white/50">
                <th className="text-left px-2 py-2 font-mono uppercase text-[10px]">Variant</th>
                <th className="text-left px-2 py-2 font-mono uppercase text-[10px]">From</th>
                <th className="text-left px-2 py-2 font-mono uppercase text-[10px]">To</th>
                <th className="text-left px-2 py-2 font-mono uppercase text-[10px]">Accuracy</th>
                <th className="text-left px-2 py-2 font-mono uppercase text-[10px]">Serves</th>
                <th className="text-left px-2 py-2 font-mono uppercase text-[10px]">When</th>
              </tr>
            </thead>
            <tbody>
              {changes.map((c) => (
                <tr key={c.change_id} className="border-b border-nk-line/40"
                    data-testid={`tuning-change-${c.change_id}`}>
                  <td className="px-2 py-2 font-mono text-white/60">{c.variant_id.slice(0, 14)}</td>
                  <td className="px-2 py-2 capitalize">{c.from_difficulty}</td>
                  <td className="px-2 py-2 capitalize text-nk-gold">→ {c.to_difficulty}</td>
                  <td className="px-2 py-2">{c.live_accuracy_pct}%</td>
                  <td className="px-2 py-2">{c.times_answered}</td>
                  <td className="px-2 py-2 text-white/40">{(c.at || "").slice(0, 16)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className="border border-nk-line rounded-sm p-2">
      <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">{label}</div>
      <div className={`font-display text-xl ${accent ? "text-nk-gold" : "text-nk-text"}`}>{value}</div>
    </div>
  );
}

